import { useState, useEffect, useMemo, useRef } from "react";
import { CustomColumn, PrimaryTableHeaderProps } from "..";
import { DropdownOption } from "@agilant/toga-blox/dist/components/SearchInput/SearchNumberInput";
import { FILTERFIELDS } from "../PrimaryTable/FIELDS/FILTERFIELDS";
import { getFilterConfigByType } from "../../../../utils";
import { SearchCriterion } from "./useConstructUrl"; // adjust path if needed

interface UsePrimaryTableHeaderModelProps<T extends object> {
	headerGroups: PrimaryTableHeaderProps<T>["headerGroups"];
	columnMappings: PrimaryTableHeaderProps<T>["columnMappings"];
	onHeaderSortClick: PrimaryTableHeaderProps<T>["onHeaderSortClick"];
	editingHeader: number | null;
	setEditingHeader: PrimaryTableHeaderProps<T>["setEditingHeader"];
	slug: string;
	searchCriteria: any[];
	setSearchCriteria: React.Dispatch<React.SetStateAction<any[]>>;
}

function usePrimaryTableHeaderModel<T extends object>({
	headerGroups,
	columnMappings,
	onHeaderSortClick,
	editingHeader,
	searchCriteria,
	setSearchCriteria,
}: UsePrimaryTableHeaderModelProps<T>) {
	// Existing state
	const [activeColumns, setActiveColumns] = useState<number[]>([]);
	const [selectedValue, setSelectedValue] = useState<DropdownOption[]>([]);
	const [hasOperator, setHasOperator] = useState<boolean>(false);
	const [operatorColumnIds, setOperatorColumnIds] = useState<string[]>([]);
	const storageKey = "searchCriteria";
	const containerRef = useRef<HTMLDivElement>(null);
	const magnifyingIconRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	// --- New State: Dropdown operator selected option ---
	const [selectedOption, setSelectedOption] = useState<DropdownOption>({
		label: "",
		value: "",
		uuid: "",
	});

	// --- New State: Date picker values ---
	const [minValue, setMinValue] = useState<string>("");
	const [maxValue, setMaxValue] = useState<string>("");
	const [selectedDate, setSelectedDate] = useState<Date | undefined>();
	const [selectedStartDate, setSelectedStartDate] = useState<
		Date | undefined
	>();
	const [selectedEndDate, setSelectedEndDate] = useState<Date | undefined>();

	const { headerNames, mappingFields } = useMemo(() => {
		const names: string[] = [];
		headerGroups.forEach((headerGroup) => {
			headerGroup.headers.forEach((col) => {
				let name = "";
				if (typeof col.Header === "string") {
					name = col.Header;
				} else if (
					typeof col.Header === "function" &&
					col.Header.displayName
				) {
					name = col.Header.displayName;
				}
				names.push(name);
			});
		});
		let fields: string[] = [];
		if (columnMappings && Array.isArray(columnMappings)) {
			const sorted = [...columnMappings].sort(
				(a, b) => (a.index ?? 0) - (b.index ?? 0)
			);
			fields = sorted.map((cm) => {
				const field = cm.slug || "";
				return field.replace(/^_+/, "");
			});
		}
		return { headerNames: names, mappingFields: fields };
	}, [headerGroups, columnMappings]);

	// --- Sync active columns from localStorage (unchanged) ---
	useEffect(() => {
		const stored = localStorage.getItem(storageKey);
		if (stored) {
			try {
				const parsed = JSON.parse(stored);
				if (Array.isArray(parsed)) {
					const matchedIndices: number[] = [];
					parsed.forEach((criteriaObj: any) => {
						const { id } = criteriaObj?.searchColumn || {};
						if (id) {
							headerGroups.forEach((headerGroup) => {
								headerGroup.headers.forEach((col, colIndex) => {
									if (col.id === id) {
										matchedIndices.push(colIndex);
									}
								});
							});
						}
					});
					const uniqueIndices = [...new Set(matchedIndices)];
					setActiveColumns(uniqueIndices);
				}
			} catch (error) {
				console.error(
					"Failed to parse 'searchCriteria' from localStorage:",
					error
				);
			}
		}
	}, [headerGroups]);

	// --- Save searchCriteria to localStorage ---
	// useEffect(() => {
	// 	try {
	// 		localStorage.setItem(storageKey, JSON.stringify(searchCriteria));
	// 	} catch (error) {
	// 		console.error("Failed to save searchCriteria:", error);
	// 	}
	// }, [searchCriteria, storageKey]);

	// --- Listen for storage changes ---
	useEffect(() => {
		const handleStorageChange = (event: StorageEvent) => {
			if (event.key === storageKey) {
				const newValue = event.newValue
					? JSON.parse(event.newValue)
					: [];
				setSearchCriteria(newValue);
			}
		};
		window.addEventListener("storage", handleStorageChange);
		return () => window.removeEventListener("storage", handleStorageChange);
	}, [storageKey, setSearchCriteria]);

	//  Determine selectedOption from searchCriteria
	useEffect(() => {
		if (
			editingHeader !== null &&
			searchCriteria &&
			headerGroups &&
			headerGroups.length > 0
		) {
			const editingColumn = headerGroups[0].headers[editingHeader] as any;
			if (!editingColumn) return;

			// For STATUS columns, force the dropdown option.
			if (editingColumn?.type?.toUpperCase() === "STATUS") {
				const forcedStatusOption: DropdownOption = {
					label: "Includes",
					value: "in:",
					uuid: "status_in",
				};
				setSelectedOption(forcedStatusOption);
				return;
			}

			const filterConfig = getFilterConfigByType(
				editingColumn.type,
				FILTERFIELDS
			);
			const customDropDownOptions =
				filterConfig.dropDownOptions as DropdownOption[];

			let criteriaText = "";
			if (typeof searchCriteria === "string") {
				criteriaText = searchCriteria;
			} else if (Array.isArray(searchCriteria)) {
				const criteriaArray = searchCriteria as SearchCriterion[];
				const matchingCriteria = criteriaArray.filter(
					(crit) => crit.searchColumn?.id === editingColumn.id
				);
				if (matchingCriteria.length >= 1) {
					const selectedCriterion =
						matchingCriteria.length > 1
							? matchingCriteria.find((crit) => {
									const opMatch =
										typeof crit.submittedSearchText ===
										"string"
											? crit.submittedSearchText.match(
													/^(.*?:)/
											  )
											: crit.submittedSearchText[0].match(
													/^(.*?:)/
											  );
									return opMatch && opMatch[1] !== "eq:";
							  }) || matchingCriteria[0]
							: matchingCriteria[0];
					if (selectedCriterion) {
						if (
							typeof selectedCriterion.submittedSearchText ===
							"string"
						) {
							criteriaText =
								selectedCriterion.submittedSearchText;
						} else {
							criteriaText =
								selectedCriterion.submittedSearchText[0];
						}
					}
				}
			} else if (
				typeof searchCriteria === "object" &&
				searchCriteria !== null
			) {
				const criterionObj = searchCriteria as SearchCriterion;
				if (typeof criterionObj.submittedSearchText === "string") {
					criteriaText = criterionObj.submittedSearchText;
				} else if (Array.isArray(criterionObj.submittedSearchText)) {
					criteriaText = criterionObj.submittedSearchText[0];
				}
			}
			if (criteriaText) {
				const operatorMatch = criteriaText.match(/^(.*?:)/);
				if (operatorMatch) {
					const operatorValue = operatorMatch[1];
					const matchingOption = customDropDownOptions.find(
						(opt) => opt.value === operatorValue
					);
					if (matchingOption) {
						setSelectedOption(matchingOption);
					} else {
						setSelectedOption(customDropDownOptions[0]);
					}
				} else {
					setSelectedOption(customDropDownOptions[0]);
				}
			} else {
				setSelectedOption(customDropDownOptions[0]);
			}
		}
	}, [editingHeader, searchCriteria, headerGroups]);

	useEffect(() => {
		if (editingHeader == null) return;
		const editingColumn = headerGroups[0]?.headers[
			editingHeader
		] as CustomColumn<T>;
		if (!editingColumn) return;
		const colId = editingColumn.id;
		if (!colId) return;

		const found = searchCriteria.find(
			(crit) => crit.searchColumn?.id === colId
		);
		if (!found) {
			setSelectedValue([]);
			return;
		}
		const text = found.submittedSearchText;
		const raw = Array.isArray(text) ? text.join(",") : text;

		const noOperator = raw.replace(/^[^:]+:/, "");
		const splitted = noOperator.split(",");
		const filterConfig = getFilterConfigByType(
			editingColumn.type,
			FILTERFIELDS
		);
		const possibleOptions = filterConfig.dropDownOptions || [];
		const rehydratedItems = splitted.map((val: string) => {
			const match = possibleOptions.find(
				(opt) => opt.value === val.trim()
			);
			return match
				? { label: match.label, value: match.value }
				: { label: val.trim(), value: val.trim() };
		});

		setSelectedValue(rehydratedItems);
	}, [editingHeader, headerGroups, searchCriteria, setSelectedValue]);

	// --- Effect for operator flags
	useEffect(() => {
		if (searchCriteria && searchCriteria.length > 0) {
			const validOperatorItems = searchCriteria.filter((item) => {
				if (item.submittedSearchText) {
					const colonIndex = item.submittedSearchText.indexOf(":");
					return (
						colonIndex > 0 &&
						item.searchColumn &&
						item.searchColumn.id
					);
				}
				return false;
			});
			const operatorExists = validOperatorItems.length > 0;
			setHasOperator(operatorExists);
			if (operatorExists) {
				const ids = validOperatorItems.map(
					(item) => item.searchColumn.id
				);
				setOperatorColumnIds(ids);
			} else {
				setOperatorColumnIds([]);
			}
		} else {
			setHasOperator(false);
			setOperatorColumnIds([]);
		}
	}, [searchCriteria]);

	const handleSortChange = (
		columnId: string,
		direction: "asc" | "desc" | null
	) => {
		onHeaderSortClick(columnId, direction);
	};

	return {
		activeColumns,
		setActiveColumns,
		headerNames,
		mappingFields,
		containerRef,
		magnifyingIconRef,
		inputRef,
		handleSortChange,
		selectedValue,
		setSelectedValue,
		hasOperator,
		operatorColumnIds,
		selectedOption,
		setSelectedOption,
		minValue,
		setMinValue,
		maxValue,
		setMaxValue,
		selectedDate,
		setSelectedDate,
		selectedStartDate,
		setSelectedStartDate,
		selectedEndDate,
		setSelectedEndDate,
	};
}

export default usePrimaryTableHeaderModel;
